import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const user = await login(form.email, form.password);
      toast.success("Welcome back!");
      const dest = location.state?.from || (user.role === "admin" ? "/admin" : "/dashboard");
      navigate(dest);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4">
      <div className="sadd-aurora" />
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo /></div>
        <div className="glass rounded-2xl p-8">
          <h1 className="font-heading text-2xl font-bold text-white mb-1">Welcome back</h1>
          <p className="text-sm text-slate-400 mb-6">Login to manage your subscription</p>
          {error && <div data-testid="login-error" className="mb-4 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2">{error}</div>}
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-slate-300">Email</Label>
              <Input data-testid="login-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 bg-white/5 border-white/10 text-white" placeholder="you@example.com" />
            </div>
            <div>
              <Label className="text-slate-300">Password</Label>
              <Input data-testid="login-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 bg-white/5 border-white/10 text-white" placeholder="••••••••" />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full glow-btn bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Login"}
            </Button>
          </form>
          <p className="text-sm text-slate-400 mt-6 text-center">
            No account? <Link to="/register" className="text-neon-purple hover:underline">Create one</Link>
          </p>
        </div>
        <p className="text-center text-xs text-slate-600 mt-6">
          <Link to="/" className="hover:text-slate-400">← Back to home</Link>
        </p>
      </div>
    </div>
  );
}
